import { Alert, Button, Col, Divider, Form, InputNumber, Row, Tag, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useWisecureMutation } from '@wisecure/api-client/hooks';
import type {
  SecurityCatalogResponse,
  SecurityPricingSettingsResponse,
  UpdateSecurityPricingSettingsDto,
} from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { WisecureCard } from '@/components/base';

type SettingKey = keyof UpdateSecurityPricingSettingsDto;

interface FieldDef {
  name: SettingKey;
  label: string;
  unit: string;
  help: string;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
}

const LABOUR_FIELDS: FieldDef[] = [
  { name: 'alarmHourlyRate', label: 'Taux horaire alarme', unit: '€/h', help: "Main d'œuvre facturée au client pour la pose d'une alarme." },
  { name: 'videoHourlyRate', label: 'Taux horaire vidéo', unit: '€/h', help: "Main d'œuvre facturée au client pour la pose de la vidéo." },
  { name: 'halfDayHours', label: "Durée d'une demi-journée", unit: 'h', min: 0.5, step: 0.5, help: "La pose est facturée par demi-journées entières." },
  { name: 'toleranceHours', label: 'Tolérance', unit: 'h', step: 0.25, help: "Dépassement accepté avant de facturer une demi-journée de plus." },
  { name: 'alarmSetupMinutes', label: 'Mise en service alarme', unit: 'min', precision: 0, help: "Temps fixe ajouté à toute pose d'alarme (paramétrage, tests)." },
  { name: 'videoSetupMinutes', label: 'Mise en service vidéo', unit: 'min', precision: 0, help: 'Temps fixe ajouté à toute pose de vidéo (réseau, application).' },
];

const FEE_FIELDS: FieldDef[] = [
  { name: 'commissioningFee', label: 'Forfait mise en service', unit: '€', help: 'Ajouté une fois à chaque installation (mise en service et prise en main).' },
  { name: 'repairVisitFee', label: 'Forfait dépannage', unit: '€', help: 'Déplacement + diagnostic, affiché au client pour une demande de dépannage.' },
  { name: 'contingencyPct', label: 'Provision aléas', unit: '%', max: 100, help: "Ajoutée au prix final, remboursée au client si elle n'est pas utilisée." },
];

const INTERNAL_FIELDS: FieldDef[] = [
  { name: 'installerDayCost', label: 'Coût installateur / jour', unit: '€', help: "Rémunération de l'installateur par jour, déplacement compris. Sert au calcul de la marge." },
  { name: 'suppliesMarginPct', label: 'Marge sur fournitures', unit: '%', help: 'Marge incluse dans le prix des fournitures : sert à en déduire le coût réel.' },
];

function SettingField({ field }: { field: FieldDef }) {
  return (
    <Col xs={24} md={12}>
      <Form.Item
        name={field.name}
        label={field.label}
        extra={field.help}
        rules={[{ required: true, message: 'Valeur requise' }]}
      >
        <InputNumber<number>
          min={field.min ?? 0}
          max={field.max}
          step={field.step}
          precision={field.precision}
          suffix={field.unit}
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Col>
  );
}

const fieldGroup = (fields: FieldDef[]) => (
  <Row gutter={16}>
    {fields.map((field) => (
      <SettingField key={field.name} field={field} />
    ))}
  </Row>
);

/** Global pricing parameters of the configurator. Remount it (key) when the saved settings change. */
export function PricingSettingsCard({ settings }: { settings: SecurityPricingSettingsResponse }) {
  const [form] = Form.useForm<UpdateSecurityPricingSettingsDto>();
  const queryClient = useQueryClient();

  const update = useWisecureMutation<SecurityPricingSettingsResponse, UpdateSecurityPricingSettingsDto>(
    (client, body) => client.dashboardSecurity.securityDashboardControllerUpdateSettings({ requestBody: body }),
    {
      onSuccess: (saved) => {
        queryClient.setQueryData<SecurityCatalogResponse>(queryKeys.securityCatalog, (old) =>
          old ? { ...old, settings: saved } : old,
        );
        message.success('Paramètres enregistrés');
      },
      onError: () => message.error("Impossible d'enregistrer les paramètres"),
    },
  );

  const handleFinish = (values: UpdateSecurityPricingSettingsDto) => {
    const changed = (Object.keys(values) as SettingKey[]).filter((key) => values[key] !== settings[key]);
    if (!changed.length) {
      message.info('Aucune modification');
      return;
    }
    update.mutate(Object.fromEntries(changed.map((key) => [key, values[key]])));
  };

  return (
    <WisecureCard title="Paramètres de tarification" style={{ marginBottom: 24 }}>
      <Form form={form} layout="vertical" initialValues={settings} onFinish={handleFinish}>
        <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0 }}>
          Main d'œuvre
        </Divider>
        {fieldGroup(LABOUR_FIELDS)}
        <Divider orientation="left" orientationMargin={0}>
          Forfaits
        </Divider>
        {fieldGroup(FEE_FIELDS)}
        <Divider orientation="left" orientationMargin={0}>
          Coûts internes <Tag color="red" style={{ marginLeft: 8 }}>Interne</Tag>
        </Divider>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Ces valeurs servent uniquement au calcul de la marge Wisecure : elles ne sont jamais montrées aux clients."
        />
        {fieldGroup(INTERNAL_FIELDS)}
        <div style={{ textAlign: 'right' }}>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={update.isPending}>
            Enregistrer les paramètres
          </Button>
        </div>
      </Form>
    </WisecureCard>
  );
}
